'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'finance', label: 'Islamic Finance', icon: 'BanknotesIcon' },
  { id: 'legal', label: 'Halal Legal', icon: 'ScaleIcon' },
  { id: 'business', label: 'Business Strategy', icon: 'BriefcaseIcon' },
  { id: 'tech', label: 'Tech & Digital', icon: 'ComputerDesktopIcon' },
  { id: 'marketing', label: 'Halal Marketing', icon: 'MegaphoneIcon' },
  { id: 'education', label: 'Islamic Education', icon: 'AcademicCapIcon' },
  { id: 'health', label: 'Halal Health', icon: 'HeartIcon' },
  { id: 'general', label: 'General Consulting', icon: 'UserCircleIcon' },
];

const EXPERTISE_OPTIONS: Record<string, string[]> = {
  finance: ['Sukuk Structuring', 'Murabaha', 'Zakat Calculation', 'AAOIFI Standards', 'Shariah Audit', 'Islamic Banking', 'Waqf Management', 'Takaful'],
  legal: ['Halal Certification', 'Islamic Contract Law', 'Shariah Compliance', 'Family Law', 'Inheritance (Mirath)', 'Waqf Legal', 'Business Contracts'],
  business: ['Market Entry', 'Business Plans', 'Pitch Decks', 'OIC Markets', 'Halal Supply Chain', 'Startup Advisory', 'SME Growth'],
  tech: ['Web Development', 'Mobile Apps', 'AI/ML', 'Cybersecurity', 'Cloud Architecture', 'Islamic Fintech', 'E-commerce'],
  marketing: ['Halal Branding', 'Social Media', 'Content Strategy', 'SEO/SEM', 'Influencer Marketing', 'Market Research'],
  education: ['Quran Teaching', 'Arabic Language', 'Islamic Studies', 'Curriculum Design', 'Online Learning', 'Youth Education'],
  health: ['Nutrition (Halal)', 'Mental Health', 'Wellness Coaching', 'Medical Ethics', 'Herbal Medicine'],
  general: ['Project Management', 'HR Consulting', 'Operations', 'Strategic Planning', 'Change Management'],
};

const LANGUAGES = ['Arabic', 'English', 'Urdu', 'Somali', 'Indonesian', 'Malay', 'Turkish', 'French', 'Swahili', 'Bengali', 'Hausa', 'Persian'];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_SLOTS = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];

const TIMEZONES = [
  'UTC', 'Africa/Nairobi', 'Africa/Lagos', 'Africa/Cairo', 'Asia/Riyadh',
  'Asia/Dubai', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Kuala_Lumpur',
  'Asia/Jakarta', 'Asia/Istanbul', 'Europe/London', 'America/New_York',
];

const RATE_TYPES = [
  { id: 'hourly', label: 'Per Hour' },
  { id: 'project', label: 'Per Project' },
  { id: 'retainer', label: 'Monthly Retainer' },
];

const ACCOUNT_TYPES = [
  { id: 'checking', label: 'Checking' },
  { id: 'savings', label: 'Savings' },
  { id: 'business', label: 'Business' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'SAR', 'KES', 'NGN', 'PKR', 'IDR', 'MYR', 'TRY', 'EGP', 'BDT'];

const COUNTRIES = [
  'United States', 'United Kingdom', 'United Arab Emirates', 'Saudi Arabia',
  'Kenya', 'Nigeria', 'Pakistan', 'Indonesia', 'Malaysia', 'Turkey',
  'Egypt', 'Bangladesh', 'Canada', 'Australia', 'Germany', 'France',
  'South Africa', 'Ghana', 'Tanzania', 'Uganda', 'Somalia', 'Qatar', 'Kuwait',
];

// ─── Types ────────────────────────────────────────────────────────────────────
type Step = 1 | 2 | 3 | 4 | 5;

interface AvailabilitySlot {
  day: string;
  startTime: string;
  endTime: string;
}

interface FormData {
  // Step 1 – Profile
  displayName: string;
  professionalTitle: string;
  bio: string;
  location: string;
  languages: string[];
  // Step 2 – Qualifications
  category: string;
  expertiseAreas: string[];
  skills: string;
  yearsExperience: string;
  education: string;
  certifications: string;
  credentialsNotes: string;
  // Step 3 – Rates
  hourlyRateUsd: string;
  rateType: string;
  // Step 4 – Availability
  availabilityStatus: string;
  weeklyHoursAvailable: string;
  timezone: string;
  slots: AvailabilitySlot[];
  // Step 5 – Banking
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  routingNumber: string;
  swiftCode: string;
  iban: string;
  accountType: string;
  currency: string;
  country: string;
}

const EMPTY_FORM: FormData = {
  displayName: '',
  professionalTitle: '',
  bio: '',
  location: '',
  languages: [],
  category: '',
  expertiseAreas: [],
  skills: '',
  yearsExperience: '',
  education: '',
  certifications: '',
  credentialsNotes: '',
  hourlyRateUsd: '',
  rateType: 'hourly',
  availabilityStatus: 'available',
  weeklyHoursAvailable: '20',
  timezone: 'UTC',
  slots: [],
  // Banking
  accountHolderName: '',
  bankName: '',
  accountNumber: '',
  routingNumber: '',
  swiftCode: '',
  iban: '',
  accountType: 'checking',
  currency: 'USD',
  country: '',
};

// ─── Step Progress Bar ────────────────────────────────────────────────────────
const STEPS = [
  { num: 1, label: 'Profile', icon: 'UserCircleIcon' },
  { num: 2, label: 'Expertise', icon: 'AcademicCapIcon' },
  { num: 3, label: 'Rates', icon: 'BanknotesIcon' },
  { num: 4, label: 'Availability', icon: 'CalendarDaysIcon' },
  { num: 5, label: 'Banking', icon: 'BuildingLibraryIcon' },
];

function StepBar({ current }: { current: Step }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {STEPS.map((s, i) => {
        const done = current > s.num;
        const active = current === s.num;
        return (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
                  done
                    ? 'bg-primary text-primary-foreground'
                    : active
                    ? 'bg-primary/15 border-2 border-primary text-primary' :'bg-muted text-muted-foreground border-2 border-border'
                }`}
              >
                {done ? (
                  <Icon name="CheckIcon" size={18} />
                ) : (
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} />
                )}
              </div>
              <span className={`text-xs font-500 ${active ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-0.5 w-12 mx-1 mb-5 rounded-full transition-all duration-300 ${done ? 'bg-primary' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Tag Pill ─────────────────────────────────────────────────────────────────
function TagPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-500">
      {label}
      <button onClick={onRemove} className="hover:text-destructive transition-colors">
        <Icon name="XMarkIcon" size={12} />
      </button>
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ConsultantOnboardingPage() {
  const router = useRouter();
  const { user, loading } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingProfile, setExistingProfile] = useState<null | { id: string; onboarding_completed: boolean }>(null);
  const [checkingProfile, setCheckingProfile] = useState(true);

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) {
      router.push('/sign-up-login-screen?redirect=/consultant-onboarding');
    }
  }, [user, loading, router]);

  // Check for existing profile
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    supabase
      .from('consultant_profiles')
      .select('id, onboarding_completed, display_name, professional_title, bio, location, languages, category, expertise_areas, skills, years_experience, education, certifications, credentials_notes, hourly_rate_usd, rate_type, availability_status, weekly_hours_available, timezone')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(async ({ data }) => {
        if (data) {
          setExistingProfile({ id: data.id, onboarding_completed: data.onboarding_completed });
          // Pre-fill form with existing data
          const bankData = await supabase
            .from('consultant_bank_accounts')
            .select('account_holder_name, bank_name, account_number, routing_number, swift_code, iban, account_type, currency, country')
            .eq('user_id', user.id)
            .eq('is_default', true)
            .maybeSingle();

          setForm({
            displayName: data.display_name || '',
            professionalTitle: data.professional_title || '',
            bio: data.bio || '',
            location: data.location || '',
            languages: data.languages || [],
            category: data.category || '',
            expertiseAreas: data.expertise_areas || [],
            skills: (data.skills || []).join(', '),
            yearsExperience: String(data.years_experience || ''),
            education: data.education || '',
            certifications: (data.certifications || []).join(', '),
            credentialsNotes: data.credentials_notes || '',
            hourlyRateUsd: String(data.hourly_rate_usd || ''),
            rateType: data.rate_type || 'hourly',
            availabilityStatus: data.availability_status || 'available',
            weeklyHoursAvailable: String(data.weekly_hours_available || '20'),
            timezone: data.timezone || 'UTC',
            slots: [],
            accountHolderName: bankData.data?.account_holder_name || '',
            bankName: bankData.data?.bank_name || '',
            accountNumber: bankData.data?.account_number || '',
            routingNumber: bankData.data?.routing_number || '',
            swiftCode: bankData.data?.swift_code || '',
            iban: bankData.data?.iban || '',
            accountType: bankData.data?.account_type || 'checking',
            currency: bankData.data?.currency || 'USD',
            country: bankData.data?.country || '',
          });
        }
        setCheckingProfile(false);
      });
  }, [user]);

  const update = (key: keyof FormData, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleArrayItem = (key: 'languages' | 'expertiseAreas', item: string) => {
    setForm((prev) => {
      const arr = prev[key] as string[];
      return {
        ...prev,
        [key]: arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item],
      };
    });
  };

  const addSlot = () => {
    setForm((prev) => ({
      ...prev,
      slots: [...prev.slots, { day: 'Monday', startTime: '09:00', endTime: '17:00' }],
    }));
  };

  const removeSlot = (idx: number) => {
    setForm((prev) => ({ ...prev, slots: prev.slots.filter((_, i) => i !== idx) }));
  };

  const updateSlot = (idx: number, field: keyof AvailabilitySlot, value: string) => {
    setForm((prev) => {
      const slots = [...prev.slots];
      slots[idx] = { ...slots[idx], [field]: value };
      return { ...prev, slots };
    });
  };

  const validateStep = (): string => {
    if (step === 1) {
      if (!form.displayName.trim()) return 'Display name is required.';
      if (!form.professionalTitle.trim()) return 'Professional title is required.';
      if (!form.bio.trim()) return 'A short bio is required.';
    }
    if (step === 2) {
      if (!form.category) return 'Please select a service category.';
      if (form.expertiseAreas.length === 0) return 'Select at least one expertise area.';
    }
    if (step === 3) {
      if (!form.hourlyRateUsd || isNaN(Number(form.hourlyRateUsd)) || Number(form.hourlyRateUsd) <= 0)
        return 'Enter a valid rate (USD).';
    }
    if (step === 5) {
      if (!form.accountHolderName.trim()) return 'Account holder name is required.';
      if (!form.bankName.trim()) return 'Bank name is required.';
      if (!form.accountNumber.trim()) return 'Account number is required.';
      if (!form.country.trim()) return 'Country is required.';
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep((s) => (s < 5 ? ((s + 1) as Step) : s));
  };

  const handleBack = () => {
    setError('');
    setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  };

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    try {
      const supabase = createClient();
      const profilePayload = {
        user_id: user!.id,
        display_name: form.displayName.trim(),
        professional_title: form.professionalTitle.trim(),
        bio: form.bio.trim(),
        location: form.location.trim(),
        languages: form.languages,
        category: form.category,
        expertise_areas: form.expertiseAreas,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
        years_experience: parseInt(form.yearsExperience) || 0,
        education: form.education.trim(),
        certifications: form.certifications.split(',').map((s) => s.trim()).filter(Boolean),
        credentials_notes: form.credentialsNotes.trim(),
        hourly_rate_usd: parseFloat(form.hourlyRateUsd) || 0,
        rate_type: form.rateType,
        availability_status: form.availabilityStatus,
        weekly_hours_available: parseInt(form.weeklyHoursAvailable) || 20,
        timezone: form.timezone,
        onboarding_completed: true,
        is_active: true,
      };

      let profileId = existingProfile?.id;

      if (existingProfile) {
        const { error: updateErr } = await supabase
          .from('consultant_profiles')
          .update(profilePayload)
          .eq('id', existingProfile.id);
        if (updateErr) throw updateErr;
      } else {
        const { data, error: insertErr } = await supabase
          .from('consultant_profiles')
          .insert(profilePayload)
          .select('id')
          .single();
        if (insertErr) throw insertErr;
        profileId = data.id;
      }

      // Save availability slots
      if (form.slots.length > 0 && profileId) {
        await supabase
          .from('consultant_availability_slots')
          .delete()
          .eq('consultant_profile_id', profileId);

        const slotRows = form.slots.map((s) => ({
          consultant_profile_id: profileId,
          day_of_week: s.day,
          start_time: s.startTime,
          end_time: s.endTime,
          is_active: true,
        }));
        const { error: slotErr } = await supabase
          .from('consultant_availability_slots')
          .insert(slotRows);
        if (slotErr) throw slotErr;
      }

      // Save banking details
      if (form.accountHolderName.trim() && form.bankName.trim() && form.accountNumber.trim()) {
        const bankPayload = {
          user_id: user!.id,
          account_holder_name: form.accountHolderName.trim(),
          bank_name: form.bankName.trim(),
          account_number: form.accountNumber.trim(),
          routing_number: form.routingNumber.trim() || null,
          swift_code: form.swiftCode.trim() || null,
          iban: form.iban.trim() || null,
          account_type: form.accountType,
          currency: form.currency,
          country: form.country.trim(),
          is_default: true,
        };

        // Check if bank account already exists for this user
        const { data: existingBank } = await supabase
          .from('consultant_bank_accounts')
          .select('id')
          .eq('user_id', user!.id)
          .eq('is_default', true)
          .maybeSingle();

        if (existingBank) {
          const { error: bankUpdateErr } = await supabase
            .from('consultant_bank_accounts')
            .update(bankPayload)
            .eq('id', existingBank.id);
          if (bankUpdateErr) throw bankUpdateErr;
        } else {
          const { error: bankInsertErr } = await supabase
            .from('consultant_bank_accounts')
            .insert(bankPayload);
          if (bankInsertErr) throw bankInsertErr;
        }
      }

      router.push('/consultant-dashboard');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || checkingProfile) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
            <Icon name="BriefcaseIcon" size={28} className="text-primary" />
          </div>
          <h1 className="text-2xl font-700 text-foreground">
            {existingProfile?.onboarding_completed ? 'Update Your Consultant Profile' : 'Become a Consultant'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1.5">
            {existingProfile?.onboarding_completed
              ? 'Edit your profile details, expertise, and availability.' :'Set up your profile to start accepting bookings from clients.'}
          </p>
        </div>

        <StepBar current={step} />

        <div className="bg-card border border-border rounded-2xl shadow-card p-6 md:p-8">
          {/* ── Step 1: Profile ── */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-lg font-600 text-foreground mb-1">Your Profile</h2>
              <p className="text-sm text-muted-foreground mb-4">This is what clients will see on your public listing.</p>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Display Name <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) => update('displayName', e.target.value)}
                  placeholder="e.g. Yusuf Al-Rashid"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Professional Title <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.professionalTitle}
                  onChange={(e) => update('professionalTitle', e.target.value)}
                  placeholder="e.g. Islamic Finance & Sukuk Structuring Expert"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Bio <span className="text-destructive">*</span></label>
                <textarea
                  value={form.bio}
                  onChange={(e) => update('bio', e.target.value)}
                  rows={4}
                  placeholder="Describe your background, what you offer, and who you help..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">{form.bio.length}/500 characters</p>
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => update('location', e.target.value)}
                  placeholder="e.g. Kuala Lumpur, Malaysia"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-2">Languages Spoken</label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleArrayItem('languages', lang)}
                      className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                        form.languages.includes(lang)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Expertise ── */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-lg font-600 text-foreground mb-1">Qualifications & Expertise</h2>
              <p className="text-sm text-muted-foreground mb-4">Help clients find you by specifying your area of expertise.</p>

              <div>
                <label className="block text-sm font-500 text-foreground mb-2">Service Category <span className="text-destructive">*</span></label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        update('category', cat.id);
                        update('expertiseAreas', []);
                      }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-500 transition-all duration-150 ${
                        form.category === cat.id
                          ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                      }`}
                    >
                      <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={20} />
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.category && (
                <div>
                  <label className="block text-sm font-500 text-foreground mb-2">Expertise Areas <span className="text-destructive">*</span></label>
                  <div className="flex flex-wrap gap-2">
                    {(EXPERTISE_OPTIONS[form.category] || []).map((area) => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => toggleArrayItem('expertiseAreas', area)}
                        className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                          form.expertiseAreas.includes(area)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                  {form.expertiseAreas.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {form.expertiseAreas.map((a) => (
                        <TagPill key={a} label={a} onRemove={() => toggleArrayItem('expertiseAreas', a)} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Additional Skills</label>
                <input
                  type="text"
                  value={form.skills}
                  onChange={(e) => update('skills', e.target.value)}
                  placeholder="e.g. Excel, Python, Arabic Calligraphy (comma-separated)"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Years of Experience</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={form.yearsExperience}
                    onChange={(e) => update('yearsExperience', e.target.value)}
                    placeholder="e.g. 8"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Highest Education</label>
                  <input
                    type="text"
                    value={form.education}
                    onChange={(e) => update('education', e.target.value)}
                    placeholder="e.g. MBA, Al-Azhar University"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Certifications</label>
                <input
                  type="text"
                  value={form.certifications}
                  onChange={(e) => update('certifications', e.target.value)}
                  placeholder="e.g. CIPA, CIFP, AAOIFI (comma-separated)"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Credentials Notes</label>
                <textarea
                  value={form.credentialsNotes}
                  onChange={(e) => update('credentialsNotes', e.target.value)}
                  rows={3}
                  placeholder="Any additional notes about your qualifications or credentials..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                />
              </div>
            </div>
          )}

          {/* ── Step 3: Rates ── */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-lg font-600 text-foreground mb-1">Your Rates</h2>
              <p className="text-sm text-muted-foreground mb-4">Set your consulting rate. You can update this anytime.</p>

              <div>
                <label className="block text-sm font-500 text-foreground mb-2">Rate Type</label>
                <div className="flex gap-3">
                  {RATE_TYPES.map((rt) => (
                    <button
                      key={rt.id}
                      type="button"
                      onClick={() => update('rateType', rt.id)}
                      className={`flex-1 py-2.5 rounded-xl border text-sm font-500 transition-all duration-150 ${
                        form.rateType === rt.id
                          ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {rt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">
                  Rate (USD) <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground font-600 text-sm">$</span>
                  <input
                    type="number"
                    min="1"
                    value={form.hourlyRateUsd}
                    onChange={(e) => update('hourlyRateUsd', e.target.value)}
                    placeholder="150"
                    className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  A 10% platform fee applies. Clients see your full rate; you receive 90%.
                </p>
              </div>

              {/* Earnings preview */}
              {form.hourlyRateUsd && Number(form.hourlyRateUsd) > 0 && (
                <div className="bg-muted/50 rounded-xl p-4 border border-border">
                  <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">Earnings Preview</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Client pays</span>
                      <span className="font-600 text-foreground">${Number(form.hourlyRateUsd).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Platform fee (10%)</span>
                      <span className="text-destructive">−${(Number(form.hourlyRateUsd) * 0.1).toFixed(2)}</span>
                    </div>
                    <div className="h-px bg-border" />
                    <div className="flex justify-between text-sm font-700">
                      <span className="text-foreground">You receive</span>
                      <span className="text-primary">${(Number(form.hourlyRateUsd) * 0.9).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Availability ── */}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-lg font-600 text-foreground mb-1">Availability</h2>
              <p className="text-sm text-muted-foreground mb-4">Let clients know when you are available for sessions.</p>

              <div>
                <label className="block text-sm font-500 text-foreground mb-2">Current Status</label>
                <div className="flex gap-3">
                  {[
                    { id: 'available', label: 'Available', color: 'text-emerald-600' },
                    { id: 'limited', label: 'Limited', color: 'text-amber-600' },
                    { id: 'busy', label: 'Busy', color: 'text-destructive' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => update('availabilityStatus', s.id)}
                      className={`flex-1 py-2.5 rounded-xl border text-sm font-500 transition-all duration-150 ${
                        form.availabilityStatus === s.id
                          ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Hours Available / Week</label>
                  <input
                    type="number"
                    min="1"
                    max="80"
                    value={form.weeklyHoursAvailable}
                    onChange={(e) => update('weeklyHoursAvailable', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Timezone</label>
                  <select
                    value={form.timezone}
                    onChange={(e) => update('timezone', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Availability Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-500 text-foreground">Weekly Availability Slots</label>
                  <button
                    type="button"
                    onClick={addSlot}
                    className="flex items-center gap-1.5 text-xs font-500 text-primary hover:text-primary/80 transition-colors"
                  >
                    <Icon name="PlusIcon" size={14} />
                    Add Slot
                  </button>
                </div>

                {form.slots.length === 0 && (
                  <div className="border border-dashed border-border rounded-xl p-6 text-center">
                    <Icon name="CalendarDaysIcon" size={28} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No slots added yet.</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Click "Add Slot" to define your weekly availability.</p>
                  </div>
                )}

                <div className="space-y-2">
                  {form.slots.map((slot, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-muted/40 rounded-xl border border-border">
                      <select
                        value={slot.day}
                        onChange={(e) => updateSlot(idx, 'day', e.target.value)}
                        className="flex-1 px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                      >
                        {DAYS_OF_WEEK.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select
                        value={slot.startTime}
                        onChange={(e) => updateSlot(idx, 'startTime', e.target.value)}
                        className="px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                      >
                        {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <span className="text-xs text-muted-foreground">to</span>
                      <select
                        value={slot.endTime}
                        onChange={(e) => updateSlot(idx, 'endTime', e.target.value)}
                        className="px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                      >
                        {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeSlot(idx)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 5: Banking Details ── */}
          {step === 5 && (
            <div className="space-y-5">
              <div className="flex items-start gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="BuildingLibraryIcon" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-600 text-foreground">Banking Details</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">Add your bank account to receive payouts from client sessions.</p>
                </div>
              </div>

              {/* Security notice */}
              <div className="flex items-start gap-2.5 p-3.5 bg-primary/5 border border-primary/20 rounded-xl">
                <Icon name="ShieldCheckIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Your banking information is encrypted and stored securely. It is only used to process payouts to your account.
                </p>
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">
                  Account Holder Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={form.accountHolderName}
                  onChange={(e) => update('accountHolderName', e.target.value)}
                  placeholder="Full legal name as on your bank account"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">
                    Bank Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.bankName}
                    onChange={(e) => update('bankName', e.target.value)}
                    placeholder="e.g. Al Rajhi Bank"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Account Type</label>
                  <div className="flex gap-2">
                    {ACCOUNT_TYPES.map((at) => (
                      <button
                        key={at.id}
                        type="button"
                        onClick={() => update('accountType', at.id)}
                        className={`flex-1 py-2 rounded-xl border text-xs font-500 transition-all duration-150 ${
                          form.accountType === at.id
                            ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40'
                        }`}
                      >
                        {at.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">
                  Account Number <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={form.accountNumber}
                  onChange={(e) => update('accountNumber', e.target.value)}
                  placeholder="Your bank account number"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Routing Number</label>
                  <input
                    type="text"
                    value={form.routingNumber}
                    onChange={(e) => update('routingNumber', e.target.value)}
                    placeholder="US banks (9 digits)"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">SWIFT / BIC Code</label>
                  <input
                    type="text"
                    value={form.swiftCode}
                    onChange={(e) => update('swiftCode', e.target.value.toUpperCase())}
                    placeholder="e.g. RJHISARI"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors font-mono uppercase"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">IBAN</label>
                <input
                  type="text"
                  value={form.iban}
                  onChange={(e) => update('iban', e.target.value.toUpperCase())}
                  placeholder="e.g. GB29NWBK60161331926819 (for EU/UK/Middle East)"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors font-mono uppercase"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">
                    Country <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={form.country}
                    onChange={(e) => update('country', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Payout Currency</label>
                  <select
                    value={form.currency}
                    onChange={(e) => update('currency', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Payout info card */}
              <div className="bg-muted/50 rounded-xl p-4 border border-border">
                <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Payout Schedule</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="CalendarDaysIcon" size={13} className="text-primary flex-shrink-0" />
                    Payouts are processed every 7 days after session completion
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="BanknotesIcon" size={13} className="text-primary flex-shrink-0" />
                    Minimum payout threshold: $20 USD
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="ClockIcon" size={13} className="text-primary flex-shrink-0" />
                    International transfers may take 3–5 business days
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
              <Icon name="ExclamationCircleIcon" size={16} />
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeftIcon" size={16} />
              Back
            </button>

            <span className="text-xs text-muted-foreground">Step {step} of 5</span>

            {step < 5 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-all duration-150"
              >
                Continue
                <Icon name="ChevronRightIcon" size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon name="CheckIcon" size={16} />
                    {existingProfile?.onboarding_completed ? 'Save Changes' : 'Complete Setup'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
